import React from 'react'
import { Typography, RadioGroup, FormControlLabel, Radio, Button } from '@mui/material'
import { Question, QuestionFrame, RadioQuestion, YesNoQuestion, NumericTextField, RadioItems } from 'components/questions'
import { useGlobalState } from 'state'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { styled } from '@mui/material/styles'
import { getQuestionKey } from 'common/utils'
import { YesNoValue } from 'common/types'


export const Q06 = () => {
  const state = useGlobalState()

  const [q5HelpText, setQ5HelpText] = React.useState('')
  const [uploadFilesText, setUploadFilesText] = React.useState('')

  const answer1: YesNoValue = React.useMemo(() => {
    const answer = state.app.getAnswer(6, 1)
    return answer !== null ? answer.value : 'いいえ'
  }, [state.app.answers])

  const answer2 = React.useMemo(() => {
    const answer = state.app.getAnswer(6, 2)
    return answer !== null ? answer.value : {text: '', radio: ''}
  }, [state.app.answers])

  const answer5 = React.useMemo(() => {
    const answer = state.app.getAnswer(6, 5)
    return answer !== null ? answer.value : ''
  }, [state.app.answers])

  const updateAnswer2 = (field: string, updateValue: string) => {
    const data = {
      ...answer2,
      [field]: updateValue
    }
    data.text = data.text.replace(/[^0-9.-]/g, '')
    const valid = !data.radio || !data.text  ? false : true
    state.app.updateAnswer(6, 2, data, valid)
  }

  React.useEffect(() => {
    const a3 = state.app.getAnswer(6, 3)
    const a4 = state.app.getAnswer(6, 4)
    if (a3 === null || a4 === null) {
      const nextAnswers = {
        ...state.app.answers
      }
      if (a3 === null) {
        nextAnswers[getQuestionKey(6, 3)] = {value: '', valid: true}
      }
      if (a4 === null) {
        nextAnswers[getQuestionKey(6, 4)] = {value: '', valid: true}
      }
      state.app.setAnswers(nextAnswers)
    }
  }, [])

  React.useEffect(() => {
    const a3 = state.app.getAnswer(6, 3)
    const a4 = state.app.getAnswer(6, 4)
    const a5 = state.app.getAnswer(6, 5)
    if (a3 === null || a4 === null) {
      setQ5HelpText('質問3、質問4の回答が必要です。')
    } else {
      if (a3.value === 'いいえ' || a4.value === 'いいえ') {
        setQ5HelpText('')
        if (a5 !== null && a5.value === '' && a5.valid === true) {
          state.app.updateAnswer(6, 5, '', false)
        }
      } else {
        setQ5HelpText('質問3又は質問4の回答が「いいえ」の場合のみ回答が必要')
        if (a5 === null || a5.value !== '' || a5.valid === false) {
          state.app.updateAnswer(6, 5, '', true)
        }
      }
    }
  }, [state.app.answers])

  const VisuallyHiddenInput = styled('input')({
    overflow: 'hidden',
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 0,
  })

  const loadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files === null) return
    const arr = []
    for (let index = 0; index < e.target.files.length; index++) {
      const file = e.target.files[index]
      arr.push(file.name + ' (' + file.type + ')')
    }
    setUploadFilesText(arr.join('\n'))
  }

  return (
    <QuestionFrame q={6}>
      <YesNoQuestion num={1} content={'水防ラインを構築していますか。'} />

      {
        answer1 === 'はい' && (
          <>
            <Question num={2} content={'水防ラインの高さは何mですか。以下のいずれかの高さをご記入ください。'}>
              <RadioGroup
                sx={{pb: 1}}
                row
                value={answer2.radio}
                onChange={(e) => updateAnswer2('radio', (e.target as HTMLInputElement).value)}
              >
                <FormControlLabel value={'tp'} control={<Radio />} label="標高（T.P.）" />
                <FormControlLabel value={'aperture'} control={<Radio />} label="問5 ② ”開口部最下端”からの高さ" />
              </RadioGroup>
              <NumericTextField
                sx={{width: 260}}
                label={answer2.radio === 'tp' ? 'T.P.' : '開口部最下端からの高さ'}
                InputProps={{ endAdornment: <Typography variant='body1'>m</Typography> }}
                value={answer2.text}
                onChange={e => updateAnswer2('text', e.target.value)}
              />
            </Question>

            <RadioQuestion
              num={3}
              content={'水防ラインに防水板を使用している場合、防水板の高さは1.00m以下ですか。'}
              row
              items={[
                { value: 'いいえ', label: 'いいえ' },
                { value: 'はい', label: 'はい' },
                { value: '', label: '使用していない' }
              ]}
            />

            <RadioQuestion
              num={4}
              content={'水防ラインに土嚢又は水嚢を使用している場合、土嚢又は水嚢の高さは0.45m以下ですか。'}
              row
              items={[
                { value: 'いいえ', label: 'いいえ' },
                { value: 'はい', label: 'はい' },
                { value: '', label: '使用していない' }
              ]}
            />

            <Question
              num={5}
              content={'水防ラインに防水板や、土嚢、水嚢などの高さを使用するが、その高さが回答欄の選択肢を超える（③又は④の回答が「いいえ」）場合、性能書や組み方の仕様書の添付により安全性を説明できますか。\n( ※「はい」の場合、認証申し込みの際にはエビデンス資料が必要となります。 )'}
            >
              { q5HelpText !== '' && <Typography variant='body2' color='error'>{q5HelpText}</Typography> }
              { q5HelpText === '' &&
                <RadioGroup row value={answer5} onChange={(e) => state.app.updateAnswer(6, 5, e.target.value)}>
                  <RadioItems items={state.scoring.getRadioItems('q6-5')} />
                </RadioGroup>
              }
            </Question>

            <RadioQuestion
              num={6}
              content={'水防ラインの構築に関して、どのくらいの時間を要しますか。'}
              row
              items={state.scoring.getRadioItems('q6-6')}
            />

            <YesNoQuestion num={7} content={'水防ラインの構築に関して、年1回以上の動作確認又は設置訓練を行っていますか。'} />
          </>
        )
      }

    </QuestionFrame>
  )
}
